import React, { useState, useEffect } from 'react';
import { Shield, Lock, Check, X, Crown, Users, BarChart2, Search, CheckCircle, AlertTriangle, AlertCircle } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { useGroupStore } from '../store/groupStore';
import GlassCard from '../components/GlassCard';
import { useToast } from '../components/Toast';
import { adminApi, groupsApi } from '../lib/apiClient';
import { cn } from '../lib/utils';

export default function AdminPage() {
  const { user } = useAuthStore();
  const toast = useToast();

  // Gates State
  const [isVerified, setIsVerified] = useState(true);
  const [gatePassword, setGatePassword] = useState('');
  const [gateError, setGateError] = useState('');

  // Tab State
  const [activeAdminTab, setActiveAdminTab] = useState('approvals'); // 'approvals' | 'assignment' | 'stats'

  // Dynamic Group Approvals State
  const [approvalsList, setApprovalsList] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'pending' | 'approved' | 'rejected'
  const [rejectId, setRejectId] = useState(null);
  const [rejectReasonText, setRejectReasonText] = useState('');

  // Dynamic Leader Assignment State
  const [selectedGroup, setSelectedGroup] = useState('');
  const [groupAlphaMembers, setGroupAlphaMembers] = useState([]);

  // Dynamic Platform Stats State
  const [platformStats, setPlatformStats] = useState(null);

  // Lock verify gate password check
  const handleGateSubmit = (e) => {
    e.preventDefault();
    setGateError('');

    if (gatePassword === 'Arghodeep123') {
      setIsVerified(true);
      toast.success('Admin access verified successfully!');
    } else {
      setGateError('Incorrect administrator password.');
    }
  };

  // Load approvals and stats upon admin verification
  useEffect(() => {
    let active = true;
    const fetchAdminData = async () => {
      try {
        const [pendingRes, statsRes] = await Promise.all([
          adminApi.getPendingGroups().catch(() => []),
          adminApi.getStats().catch(() => null)
        ]);

        if (active) {
          if (pendingRes) {
            const mapped = pendingRes.map(g => ({
              id: g.id,
              name: g.name,
              createdBy: g.leader_name || 'Founder',
              memberCount: g.member_count || 1,
              createdDate: new Date(g.created_at * 1000).toISOString().split('T')[0],
              status: g.status
            }));
            setApprovalsList(mapped);
          }
          if (statsRes) {
            setPlatformStats(statsRes);
          }
        }
      } catch (err) {
        console.error(err);
      }
    };

    if (isVerified) {
      fetchAdminData();
    }
    return () => { active = false; };
  }, [isVerified]);

  // Load members dynamically when a group is selected
  useEffect(() => {
    let active = true;
    const fetchMembers = async () => {
      if (!selectedGroup) return;
      try {
        const res = await groupsApi.getMembers(selectedGroup).catch(() => []);
        if (active && res) {
          const mapped = res.map(m => ({
            id: m.id,
            name: m.display_name || m.name,
            role: m.role,
            joinDate: new Date(m.created_at * 1000).toISOString().split('T')[0]
          }));
          setGroupAlphaMembers(mapped);
        }
      } catch (err) {
        console.error(err);
      }
    };

    if (isVerified && selectedGroup) {
      fetchMembers();
    }
    return () => { active = false; };
  }, [selectedGroup, isVerified]);

  // Handle Approvals
  const handleApprove = async (grpId) => {
    try {
      await adminApi.approveGroup(grpId);
      setApprovalsList(prev => 
        prev.map(g => g.id === grpId ? { ...g, status: 'approved' } : g)
      );
      toast.success('Group approved successfully!');
    } catch (e) {
      toast.error('Failed to approve group.');
    }
  };

  const handleRejectSubmit = async (e) => {
    e.preventDefault();
    if (!rejectReasonText.trim()) {
      toast.error('A rejection reason is required!');
      return;
    }

    try {
      await adminApi.rejectGroup(rejectId, rejectReasonText.trim());
      setApprovalsList(prev => 
        prev.map(g => g.id === rejectId ? { ...g, status: 'rejected', rejectedReason: rejectReasonText.trim() } : g)
      );
      toast.success('Group rejected successfully.');
    } catch (e) {
      toast.error('Failed to reject group.');
    } finally {
      setRejectId(null);
      setRejectReasonText('');
    }
  };

  // Designation updates
  const handleAssignRole = async (memberId, newRole) => {
    if (!selectedGroup) return;
    try {
      if (newRole === 'leader') {
        await adminApi.setLeader(selectedGroup, memberId);
      } else {
        await adminApi.setVice(selectedGroup, memberId);
      }
      
      // Update local state to match role assignments
      setGroupAlphaMembers(prev => prev.map(m => {
        if (newRole === 'leader') {
          if (m.id === memberId) return { ...m, role: 'leader' };
          if (m.role === 'leader') return { ...m, role: 'member' };
        } else if (newRole === 'vice-leader') {
          if (m.id === memberId) return { ...m, role: 'vice-leader' };
          if (m.role === 'vice-leader') return { ...m, role: 'member' };
        }
        return m;
      }));

      toast.success(`Role updated successfully to ${newRole === 'leader' ? 'Leader' : 'Vice-Leader'}`);
    } catch (err) {
      toast.error('Failed to assign designation');
    }
  };

  // Filter Group Approvals
  const filteredApprovals = approvalsList
    .filter(g => g.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .filter(g => statusFilter === 'all' ? true : g.status === statusFilter);

  // Render Gate Screen if not verified
  if (!isVerified) {
    return (
      <div className="flex items-center justify-center min-h-[80vh] p-4 bg-[#090e1a]">
        <GlassCard className="w-full max-w-sm p-6 text-center flex flex-col items-center gap-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-red-500/5 filter blur-3xl pointer-events-none -z-10" />
          
          <div className="w-12 h-12 rounded-full flex items-center justify-center bg-red-500/10 border border-red-500/20 text-red-400">
            <Lock size={22} className="animate-pulse" />
          </div>
          
          <div>
            <h1 className="heading text-2xl font-bold text-white tracking-wide">Confirm Admin Access</h1>
            <p className="text-white/40 text-xs mt-1">Re-enter your administrator password to unlock</p>
          </div>

          <form onSubmit={handleGateSubmit} className="w-full flex flex-col gap-3 mt-2">
            <input
              type="password"
              value={gatePassword}
              onChange={(e) => setGatePassword(e.target.value)}
              placeholder="Enter admin password..."
              className="w-full rounded-xl px-4 py-3 text-sm text-center text-white placeholder-white/20 outline-none transition-all duration-200 border border-white/10 bg-white/5"
            />
            {gateError && (
              <div className="text-red-400 text-xs flex items-center justify-center gap-1.5 font-semibold animate-shake">
                <AlertCircle size={14} />
                <span>{gateError}</span>
              </div>
            )}
            
            <button type="submit" className="btn-danger w-full py-3 rounded-xl text-sm font-bold mt-2">
              Unlock Archway
            </button>
          </form>
        </GlassCard>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full p-6 md:p-8 overflow-y-auto space-y-6">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-white/5 pb-5">
        <div className="flex flex-col gap-1">
          <h1 className="heading text-3xl font-extrabold text-white tracking-wide flex items-center gap-3">
            <Shield size={28} className="text-red-500" />
            <span>High Council Chambers</span>
          </h1>
          <p className="text-white/40 text-sm">
            Govern leagues, assign appointments, view macro intelligence metrics.
          </p>
        </div>

        {/* Action tab triggers */}
        <div className="flex rounded-xl bg-white/5 border border-white/10 p-1 self-start">
          <button
            onClick={() => setActiveAdminTab('approvals')}
            className={`px-4 py-2 text-xs font-bold rounded-lg transition-all duration-300 ${
              activeAdminTab === 'approvals' ? 'bg-red-600 text-white shadow-md' : 'text-white/50 hover:text-white/80'
            }`}
          >
            Approvals
          </button>
          <button
            onClick={() => setActiveAdminTab('assignment')}
            className={`px-4 py-2 text-xs font-bold rounded-lg transition-all duration-300 ${
              activeAdminTab === 'assignment' ? 'bg-red-600 text-white shadow-md' : 'text-white/50 hover:text-white/80'
            }`}
          >
            Assignments
          </button>
          <button
            onClick={() => setActiveAdminTab('stats')}
            className={`px-4 py-2 text-xs font-bold rounded-lg transition-all duration-300 ${
              activeAdminTab === 'stats' ? 'bg-red-600 text-white shadow-md' : 'text-white/50 hover:text-white/80'
            }`}
          >
            Stats
          </button>
        </div>
      </div>

      {/* Panels */}
      {activeAdminTab === 'approvals' && (
        <div className="space-y-4 animate-fade-in">
          
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
            <div className="relative w-full max-w-xs">
              <Search size={14} className="absolute left-3 top-3 text-white/30" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search groups to review..."
                className="w-full rounded-xl pl-9 pr-4 py-2 text-xs text-white placeholder-white/20 outline-none border border-white/10"
                style={{ background: 'rgba(255,255,255,0.03)' }}
              />
            </div>

            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase font-bold text-white/40 tracking-wider">Status:</span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded-xl px-3 py-2 text-xs text-white/70 outline-none border border-white/10 bg-[#090e1a]"
              >
                <option value="all">All</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-white/10 bg-white/5 backdrop-blur-md">
            <table className="w-full border-collapse text-left text-xs">
              <thead>
                <tr className="border-b border-white/10 text-white/40 font-bold uppercase tracking-wider bg-white/[0.02]">
                  <th className="p-4">Group Name</th>
                  <th className="p-4">Founder</th>
                  <th className="p-4">Members</th>
                  <th className="p-4">Created Date</th>
                  <th className="p-4">Status</th>
                  <th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5 text-white/80 font-medium">
                {filteredApprovals.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-white/20">
                      No matching groups are currently awaiting action.
                    </td>
                  </tr>
                ) : (
                  filteredApprovals.map(grp => (
                    <tr key={grp.id} className="hover:bg-white/[0.02] transition-colors">
                      <td className="p-4 font-bold text-white">{grp.name}</td>
                      <td className="p-4 text-white/60">{grp.createdBy}</td>
                      <td className="p-4 text-white/60">{grp.memberCount} / 15</td>
                      <td className="p-4 font-mono text-white/50">{grp.createdDate}</td>
                      <td className="p-4">
                        <span className={cn(
                          "px-2 py-0.5 rounded-full font-bold text-[9px] uppercase",
                          grp.status === 'approved' && "bg-green-500/10 text-green-400 border border-green-500/20",
                          grp.status === 'pending' && "bg-amber-500/10 text-amber-400 border border-amber-500/20",
                          grp.status === 'rejected' && "bg-red-500/10 text-red-400 border border-red-500/20"
                        )}>
                          {grp.status}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        {grp.status === 'pending' ? (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleApprove(grp.id)}
                              className="p-1 text-green-400 hover:bg-green-500/10 border border-green-500/20 rounded"
                              title="Approve Group"
                            >
                              <Check size={14} />
                            </button>
                            <button
                              onClick={() => setRejectId(grp.id)}
                              className="p-1 text-red-400 hover:bg-red-500/10 border border-red-500/20 rounded"
                              title="Reject Group"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ) : (
                          <span className="text-white/20 italic text-[11px]">Decided</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {rejectId && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
              <GlassCard className="w-full max-w-sm p-5 border border-red-500/20">
                <form onSubmit={handleRejectSubmit} className="flex flex-col gap-4">
                  <div className="flex items-center gap-2 text-red-400">
                    <AlertTriangle size={18} />
                    <span className="heading font-bold text-sm">Reason for Rejection</span>
                  </div>
                  <textarea
                    value={rejectReasonText}
                    onChange={(e) => setRejectReasonText(e.target.value)}
                    placeholder="Enter explicit reason why this group was rejected..."
                    rows={3}
                    className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-xs text-white placeholder-white/25 outline-none focus:border-red-500/60"
                  />
                  <div className="flex justify-end gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => setRejectId(null)}
                      className="px-3 py-1.5 rounded-lg text-white/40 hover:text-white/80 text-xs transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-danger px-4 py-1.5 rounded-lg text-xs font-bold"
                    >
                      Log Rejection
                    </button>
                  </div>
                </form>
              </GlassCard>
            </div>
          )}
        </div>
      )}

      {activeAdminTab === 'assignment' && (
        <div className="space-y-4 animate-fade-in">
          
          <div className="p-4 rounded-xl bg-white/5 border border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-4 backdrop-blur-md">
            <div>
              <h3 className="heading text-md font-bold text-white">Select Academic Alliance</h3>
              <p className="text-white/30 text-xs">Assign designation leaders per league.</p>
            </div>
            
            <input
              type="text"
              value={selectedGroup}
              onChange={(e) => setSelectedGroup(e.target.value)}
              placeholder="Enter Group ID to assign..."
              className="rounded-xl px-4 py-2.5 text-xs text-white outline-none border border-white/10 bg-[#090e1a] font-bold"
            />
          </div>

          <div className="space-y-3">
            {groupAlphaMembers.length === 0 ? (
              <div className="text-white/20 text-xs py-8 text-center bg-white/5 rounded-xl border border-white/10">
                Please input a valid Group ID above to see members.
              </div>
            ) : (
              groupAlphaMembers.map(member => {
                const isLeader = member.role === 'leader';
                const isVice = member.role === 'vice-leader' || member.role === 'vice_leader';
                
                return (
                  <div key={member.id} className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/[0.07] transition-all duration-200">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center bg-white/5 border border-white/5">
                        {isLeader ? (
                          <Crown size={16} className="text-amber-500" />
                        ) : isVice ? (
                          <Crown size={16} className="text-indigo-400" />
                        ) : (
                          <Users size={14} className="text-white/20" />
                        )}
                      </div>

                      <div>
                        <div className="text-sm font-semibold text-white flex items-center gap-2">
                          <span>{member.name}</span>
                          {isLeader && <span className="text-[9px] font-bold text-amber-500 uppercase px-1.5 py-0.5 rounded bg-amber-500/10 border border-amber-500/20">Leader</span>}
                          {isVice && <span className="text-[9px] font-bold text-indigo-400 uppercase px-1.5 py-0.5 rounded bg-indigo-500/10 border border-indigo-500/20">Vice-Leader</span>}
                        </div>
                        <div className="text-white/30 text-xs font-mono mt-0.5">Joined: {member.joinDate}</div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {!isLeader && (
                        <button
                          onClick={() => handleAssignRole(member.id, 'leader')}
                          className="px-3 py-1.5 rounded-lg border border-amber-500/30 text-amber-400 hover:bg-amber-500/10 text-xs font-semibold transition-colors"
                        >
                          Set Leader
                        </button>
                      )}
                      {!isVice && (
                        <button
                          onClick={() => handleAssignRole(member.id, 'vice-leader')}
                          className="px-3 py-1.5 rounded-lg border border-indigo-500/30 text-indigo-400 hover:bg-indigo-500/10 text-xs font-semibold transition-colors"
                        >
                          Set Vice-Leader
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {activeAdminTab === 'stats' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in">
          
          {/* Total Users */}
          <GlassCard className="p-5 flex flex-col gap-3">
            <span className="text-white/30 text-xs uppercase tracking-widest font-bold">Total Users</span>
            <div className="flex items-baseline justify-between mt-1">
              <span className="heading text-4xl font-extrabold text-white">
                {platformStats?.total_users ?? 0}
              </span>
            </div>
          </GlassCard>

          {/* Total Groups */}
          <GlassCard className="p-5 flex flex-col gap-3">
            <span className="text-white/30 text-xs uppercase tracking-widest font-bold">Approved Leagues</span>
            <div className="flex items-baseline justify-between mt-1">
              <span className="heading text-4xl font-extrabold text-white">
                {platformStats?.total_groups ?? 0}
              </span>
            </div>
          </GlassCard>

          {/* Active Sessions */}
          <GlassCard className="p-5 flex flex-col gap-3">
            <span className="text-white/30 text-xs uppercase tracking-widest font-bold">Studying Now</span>
            <div className="flex items-baseline justify-between mt-1">
              <span className="heading text-4xl font-extrabold text-indigo-400 flex items-center gap-2">
                <span>{platformStats?.active_sessions ?? 0}</span>
                {platformStats?.active_sessions > 0 && (
                  <span className="w-2.5 h-2.5 rounded-full bg-green-500 animate-ping inline-block" />
                )}
              </span>
            </div>
          </GlassCard>

          {/* Total Hours */}
          <GlassCard className="p-5 flex flex-col gap-3">
            <span className="text-white/30 text-xs uppercase tracking-widest font-bold">Cumulative Study Hours</span>
            <div className="flex items-baseline justify-between mt-1">
              <span className="heading text-4xl font-extrabold text-white">
                {Math.round((platformStats?.total_seconds || 0) / 3600)}
              </span>
              <span className="text-white/20 text-xs font-semibold font-mono">hours</span>
            </div>
          </GlassCard>

          {/* Top Group */}
          <GlassCard className="p-5 flex flex-col gap-3 lg:col-span-2">
            <span className="text-white/30 text-xs uppercase tracking-widest font-bold">Apex League This Week</span>
            <div className="flex items-center justify-between mt-1">
              <div>
                <span className="heading text-xl font-extrabold text-indigo-400">
                  {platformStats?.top_group_name || 'No Group'}
                </span>
                <p className="text-white/40 text-xs mt-0.5">Leading the platform this week</p>
              </div>
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-indigo-500/10 text-indigo-400">
                <BarChart2 size={20} />
              </div>
            </div>
          </GlassCard>

        </div>
      )}

    </div>
  );
}
